import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader } from 'src/components/atoms/Loader';
import { Carousel, CarrouselRefType } from 'src/components/molecules/Carousel';
import { DATE_PICKER_FORMAT } from 'src/components/molecules/DatePicker';
import useUserRoles from 'src/features/auth/hooks/useUserRoles';
import { defaultStateIVFields, MISMATCH_CODES } from 'src/features/incomeVerification';
import { CalculationDocument } from 'src/features/incomeVerification/constants';
import {
  useCalculationDocuments,
  useDisputeDocuments
} from 'src/features/incomeVerification/hooks';
import {
  enrichDocumentWithCalculation,
  formatApiDate
} from 'src/features/incomeVerification/utils';
import { useEntryById } from 'src/features/entries/hooks';
import api from 'src/features/incomeVerification/api';
import { useProofsFilteredByEntryId, useZeroProofsLogging } from 'src/features/proofs/hooks';
import IncomeVerificationForm from 'src/pages/IncomeVerificationFlowPage/components/IncomeVerificationForm/IncomeVerificationForm';
import { IncomeVerificationEnum } from 'src/ts/enums/session';
import { IProof } from 'src/ts/interfaces';
import {
  AutomatedProofEntry,
  AutomatedProofsResponse,
  IncomeVerification
} from 'src/ts/interfaces/incomeVerification';

type AutomatedFormFields = {
  payPeriodStartDate: string;
  payPeriodEndDate: string;
  payPeriodDateRange: string;
  recipientName: string;
  grossPay: string;
  incomeSource: string;
  hasNonRecurringIncome: boolean;
  isAutomated: boolean;
  basePayIncomeAmount?: string;
};

export const buildInitialDocument = (
  proof: IProof,
  proofData: AutomatedProofEntry | undefined,
  index: number,
  isCalcDispute: boolean,
  calculationDocuments: CalculationDocument[]
): IncomeVerification => {
  const isAutomated = proofData?.automated === true;
  const extractionData = proofData?.data;

  const formatIsoDate = (iso: string | null) => {
    if (!iso) return '';
    return format(new Date(iso.replace(/-/g, '/')), DATE_PICKER_FORMAT);
  };

  const startDate = isAutomated ? formatIsoDate(extractionData?.document_start_date ?? null) : '';
  const endDate = isAutomated ? formatIsoDate(extractionData?.document_end_date ?? null) : '';

  const automatedFields: AutomatedFormFields = isAutomated
    ? {
        payPeriodStartDate: startDate,
        payPeriodEndDate: endDate,
        payPeriodDateRange: startDate && endDate ? `${startDate} - ${endDate}` : '',
        recipientName: extractionData?.applicant_name || '',
        grossPay: extractionData?.gross_income || '',
        incomeSource: extractionData?.income_source || '',
        hasNonRecurringIncome: extractionData?.has_non_recurring_income ?? false,
        isAutomated: true
      }
    : ((): AutomatedFormFields => {
        const calcDoc = isCalcDispute
          ? calculationDocuments.find((cd) => cd.document_id === proof.id)
          : undefined;
        if (calcDoc) {
          const cStart = calcDoc.document_start_date
            ? formatApiDate(calcDoc.document_start_date)
            : '';
          const cEnd = calcDoc.document_end_date ? formatApiDate(calcDoc.document_end_date) : '';
          return {
            payPeriodStartDate: cStart,
            payPeriodEndDate: cEnd,
            payPeriodDateRange: cStart && cEnd ? `${cStart} - ${cEnd}` : '',
            recipientName: calcDoc.applicant_name || '',
            grossPay: calcDoc.gross_income?.toString() || '',
            incomeSource: calcDoc.income_source || '',
            hasNonRecurringIncome: false,
            isAutomated: false
          };
        }
        return { ...defaultStateIVFields, isAutomated: false };
      })();

  return {
    index,
    id: proof.id,
    docId: proof.short_id ?? '',
    file: proof.file || '',
    fileName: proof.fileName || '',
    ...automatedFields,
    type: proof.type
  };
};

function savePreviousDocumentsToSession(documents: IncomeVerification[], key: string) {
  window.sessionStorage.setItem(
    `${IncomeVerificationEnum.INCOME_VERIFICATION_DOCUMENTS}${key}`,
    JSON.stringify(documents)
  );
}
/**
 * Makes sure that the documents have data needed from the proofs to render the preview correctly
 *
 * @param proofs
 * @param documents
 */
function mergeProofsAndDocuments(
  proofs: IProof[],
  documents: IncomeVerification[]
): IncomeVerification[] {
  const proofsAndDocuments = proofs.reduce((acc: IncomeVerification[], proof: IProof) => {
    const document = documents.find((doc: IncomeVerification) => doc.id === proof.id);
    if (document) {
      acc.push({
        ...document,
        docId: proof.short_id || '',
        file: proof.file || '',
        fileName: proof.fileName || '',
        type: proof.type
      });
    }
    return acc;
  }, []);
  return proofsAndDocuments;
}

const IncomeVerificationFlowPage = () => {
  const { entryId, incomeVerificationReviewId } = useParams();
  const isCalcDispute = !incomeVerificationReviewId;
  const sessionKey = incomeVerificationReviewId ?? `calc-dispute-${entryId}`;
  const [searchParams] = useSearchParams();
  const carrouselRef = useRef<CarrouselRefType | null>(null);
  const documentsFromSession = window.sessionStorage.getItem(
    `${IncomeVerificationEnum.INCOME_VERIFICATION_DOCUMENTS}${sessionKey}`
  );

  // When the "Disputes" workflow is triggered, the redirectPath is provided to return the user to folder page
  const redirectPath = searchParams.get('redirectPath');
  const { entry } = useEntryById(entryId ?? '');
  const {
    proofs,
    isLoading: isLoadingProofs,
    isSuccess: isProofsSuccess
  } = useProofsFilteredByEntryId(entryId ?? '', true);
  const [documents, isLoadingAppeal] = useDisputeDocuments({
    entryId: entryId ?? '',
    // If the redirectPath is provided, this indicates that the documents should be fetched because we are in the disputes workflow
    shouldFetchDocuments: !isCalcDispute && Boolean(redirectPath) && !documentsFromSession
  });

  const { isAdmin } = useUserRoles();
  const showOverrides = isCalcDispute || !!isAdmin;

  const applicantName = [entry?.applicant?.first_name, entry?.applicant?.last_name]
    .filter(Boolean)
    .join(' ');
  const applicantId = entry?.applicant?.id;
  const companyId = entry?.folder?.company_id;
  const propertyId = entry?.folder?.property_id;
  const {
    isLoadingDocs: isLoadingCalcDocs,
    calculationDocuments,
    documentErrorsMap,
    mismatchNamesMap,
    overrideSelections,
    setOverrideSelections
  } = useCalculationDocuments({
    entryId,
    enabled: Boolean(redirectPath) && showOverrides,
    applicantName
  });

  const toggleInclude = useCallback(
    (docId: string) => {
      setOverrideSelections((prev) => ({ ...prev, [docId]: !prev[docId] }));
    },
    [setOverrideSelections]
  );

  const [proofAutomationData, setProofAutomationData] = useState<AutomatedProofsResponse>({});
  const [automationFetchDone, setAutomationFetchDone] = useState(false);

  const sessionDocs: IncomeVerification[] | null = useMemo(() => {
    try {
      const parsed = documentsFromSession ? JSON.parse(documentsFromSession) : null;
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }, [documentsFromSession]);

  const sessionHasAutomationData =
    sessionDocs !== null && sessionDocs.some((doc) => doc.isAutomated === true);

  // PS-7161: a session written by the pre-fix build for a fully automated entry holds
  // ONLY isAutomated docs; the carousel filter empties it. Discard it and rebuild from
  // a fresh fetch. Any other session is healthy and wins.
  const sessionBroken =
    sessionDocs !== null && sessionDocs.length > 0 && sessionDocs.every((doc) => doc.isAutomated);

  // Only skip the automation fetch if the session has automation data applied and is healthy
  const skipAutomationFetch = sessionHasAutomationData && !sessionBroken;

  useEffect(() => {
    if (!entryId) return;
    if (skipAutomationFetch) {
      setAutomationFetchDone(true);
      return;
    }

    setAutomationFetchDone(false);
    let cancelled = false;
    api
      .getAutomatedProofs(entryId)
      .then((response) => {
        if (!cancelled) setProofAutomationData(response?.data || {});
      })
      .catch((error) => {
        console.error('[IV Flow] Failed to fetch automated proof data:', error);
        if (!cancelled) setProofAutomationData({});
      })
      .finally(() => {
        if (!cancelled) setAutomationFetchDone(true);
      });

    return () => {
      cancelled = true;
    };
  }, [entryId, skipAutomationFetch]);

  // Prevent rendering the carousel before automation data or calc doc data is resolved
  const awaitingAutomation = !skipAutomationFetch && !automationFetchDone;
  const awaitingCalcDocs = isCalcDispute && isLoadingCalcDocs;
  const isLoading =
    (!isCalcDispute && isLoadingAppeal) ||
    isLoadingProofs ||
    awaitingAutomation ||
    awaitingCalcDocs;
  useEffect(() => {
    if (
      !isCalcDispute &&
      documents &&
      documents.length > 0 &&
      incomeVerificationReviewId &&
      proofs.length > 0
    ) {
      // Make sure that essential data from the proofs is present in the documents object so that the preview renders correctly
      const proofsInDocuments = mergeProofsAndDocuments(proofs, documents);
      savePreviousDocumentsToSession(proofsInDocuments, incomeVerificationReviewId);
    }
  }, [documents, incomeVerificationReviewId, proofs]);

  // Safety net logging for zero-proofs detection (ITL-2297)
  useZeroProofsLogging({
    workflow: 'income_verification',
    entryId,
    isProofsSuccess,
    proofsLength: proofs.length,
    folderId: entry?.folder_id,
    incomeVerificationReviewId
  });

  const route = isCalcDispute
    ? `/my-work/calculation-dispute/${entryId}/calculate${
        redirectPath ? `?redirectPath=${redirectPath}` : ''
      }`
    : `/my-work/calculateIncome/${incomeVerificationReviewId}/${entryId}${
        redirectPath ? `?redirectPath=${redirectPath}` : ''
      }`;
  const initialDocuments = useMemo(() => {
    const renderableProofs = proofs.filter((proof) => proof.short_id);

    // PS-7161: when EVERY document is automated the carousel filter below would leave
    // the analyst nothing to audit, so present them as ordinary manual documents.
    // income_verification_review_proofs.automated is untouched; the QA-audit record survives.
    const allAutomated =
      renderableProofs.length > 0 &&
      renderableProofs.every((proof) => proofAutomationData[proof.id]?.automated === true);

    return renderableProofs.map((proof, index) =>
      buildInitialDocument(
        proof,
        allAutomated ? undefined : proofAutomationData[proof.id],
        index,
        isCalcDispute,
        calculationDocuments
      )
    );
  }, [proofs, proofAutomationData, isCalcDispute, calculationDocuments]);

  const displayLastPageFromSession = window.sessionStorage.getItem(
    `${IncomeVerificationEnum.INCOME_VERIFICATION_DOCUMENTS_DISPLAY_LAST_PAGE}${sessionKey}`
  );

  const displayLastPage =
    displayLastPageFromSession !== null ? displayLastPageFromSession === 'true' : false;
  // A healthy session wins (it carries the analyst's mid-review edits); a broken
  // all-automated session or calc doc data forces a rebuild from fresh data
  const hasCalcDocData = isCalcDispute && calculationDocuments.length > 0;
  const allDocuments: IncomeVerification[] =
    sessionDocs === null || sessionBroken || hasCalcDocData ? initialDocuments : sessionDocs;

  // Filter out automated documents so analysts only see manual docs in the carousel (AC3)
  // Re-index so "Document X of Y" counts are correct for the filtered list
  const incomeVerificationDocuments = allDocuments
    .filter((doc) => !doc.isAutomated)
    .map((doc, idx) => ({ ...doc, index: idx }));

  // PS-7161 safety net: the assigned-review path should never show an empty carousel
  // while the entry has documents. Once per entry; low-cardinality fields only.
  const emptyCarousel =
    !isCalcDispute && !isLoading && proofs.length > 0 && incomeVerificationDocuments.length === 0;
  const loggedEmptyCarouselEntryRef = useRef<string | null>(null);
  useEffect(() => {
    if (!emptyCarousel || !entryId || loggedEmptyCarouselEntryRef.current === entryId) return;
    loggedEmptyCarouselEntryRef.current = entryId;
    console.error('[IV Flow] Carousel empty despite proofs on entry', {
      entryId,
      incomeVerificationReviewId,
      proofCount: proofs.length
    });
  }, [emptyCarousel, entryId, incomeVerificationReviewId, proofs.length]);

  // Save all documents (including automated) to session so automated data is preserved for calculation
  // Gate on !awaitingAutomation to prevent saving before automation data is resolved
  if (proofs.length > 0 && !awaitingAutomation) {
    window.sessionStorage.setItem(
      `${IncomeVerificationEnum.INCOME_VERIFICATION_DOCUMENTS}${sessionKey}`,
      JSON.stringify(allDocuments)
    );
  }

  const handleOnSave = useCallback(
    (data: IncomeVerification) => {
      // Tech debt (pre-existing): intentional mutation of useMemo result. Rapid
      // successive saves (debounced form fields) need each call to see the previous
      // call's changes within the same render cycle. Should be refactored to useRef or state.
      const docIndex = allDocuments.findIndex((doc) => doc.id === data.id);
      if (docIndex !== -1) {
        allDocuments[docIndex] = {
          ...data,
          payPeriodDateRange:
            data.payPeriodStartDate && data.payPeriodEndDate
              ? `${data.payPeriodStartDate} - ${data.payPeriodEndDate}`
              : ''
        };
      }

      window.sessionStorage.setItem(
        `${IncomeVerificationEnum.INCOME_VERIFICATION_DOCUMENTS}${sessionKey}`,
        JSON.stringify(allDocuments)
      );
    },
    [allDocuments, sessionKey]
  );

  const previousClicked = (currentSlide: number) => {
    window.sessionStorage.setItem(
      `${IncomeVerificationEnum.INCOME_VERIFICATION_DOCUMENTS_DISPLAY_LAST_PAGE}${sessionKey}`,
      'false'
    );
  };
  const nextClicked = (currentSlide: number) => {
    window.sessionStorage.setItem(
      `${IncomeVerificationEnum.INCOME_VERIFICATION_DOCUMENTS_DISPLAY_LAST_PAGE}${sessionKey}`,
      'false'
    );
  };

  const goToNextDocument = useCallback(() => {
    if (carrouselRef.current) {
      carrouselRef.current.nextClicked();
    }
  }, [carrouselRef]);

  if (isLoading) {
    return <Loader />;
  }

  // Payroll-only calc disputes have zero proofs — skip carousel, go directly to calculation page
  if (isCalcDispute && incomeVerificationDocuments.length === 0) {
    if (!redirectPath) {
      console.error('[IV Dispute] Missing redirectPath — cannot skip carousel');
      return <Loader />;
    }
    console.info('[IV Dispute] Skipping carousel — payroll-only submission has no documents', {
      entryId
    });
    return <Navigate to={route} replace />;
  }

  return (
    <Carousel
      ref={carrouselRef}
      routeOnLastPage={true}
      route={route}
      displayLastPageOnLoad={displayLastPage}
      onPreviousClicked={previousClicked}
      onNextClicked={nextClicked}
    >
      {incomeVerificationDocuments.map((document) => {
        const calcDoc = showOverrides
          ? calculationDocuments.find((cd) => cd.document_id === document.id)
          : undefined;
        const enrichedDoc = enrichDocumentWithCalculation(document, calcDoc);
        const docErrors = showOverrides ? documentErrorsMap[document.id] : undefined;
        const names = showOverrides ? mismatchNamesMap[document.id] : undefined;
        const hasMismatch = docErrors?.some((c) => MISMATCH_CODES.includes(c)) ?? false;
        return (
          <IncomeVerificationForm
            key={document.docId}
            currentDocument={enrichedDoc}
            totalNumberOfDocuments={incomeVerificationDocuments.length}
            onSave={handleOnSave}
            goToNextDocument={goToNextDocument}
            entryId={entryId}
            incomeVerificationReviewId={incomeVerificationReviewId}
            applicantId={applicantId}
            companyId={companyId}
            propertyId={propertyId}
            {...(showOverrides && docErrors
              ? {
                  documentErrors: docErrors,
                  applicantName: names?.applicant,
                  documentName: names?.document,
                  isIncludedInCalculation: overrideSelections[document.id],
                  onToggleInclude: hasMismatch ? () => toggleInclude(document.id) : undefined
                }
              : {})}
          ></IncomeVerificationForm>
        );
      })}
    </Carousel>
  );
};

export default IncomeVerificationFlowPage;
